import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { API_URL } from '@/lib/api';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [synced, setSynced] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { isAuthenticated, user, loginWithGoogle } = useAuth();

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('coolgirl-wishlist');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setWishlist(parsed);
      } catch (e) {
        console.error('Error parsing wishlist:', e);
      }
    }
    setInitialized(true);
  }, []);

  // Save wishlist to localStorage when it changes (but only after initialization)
  useEffect(() => {
    if (initialized) {
      localStorage.setItem('coolgirl-wishlist', JSON.stringify(wishlist));
    }
  }, [wishlist, initialized]);

  // Sync with backend when user logs in - SERVER is source of truth
  const syncWithBackend = useCallback(async () => {
    if (!isAuthenticated || synced) return;

    try {
      // Fetch favorites from server - server is the source of truth for logged-in users
      const response = await fetch(`${API_URL}/api/auth/favorites`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Replace local wishlist with server data
        setWishlist(data.favorites || []);
        setSynced(true);
      }
    } catch (error) {
      console.error('Failed to sync favorites:', error);
    }
  }, [isAuthenticated, synced]);

  useEffect(() => {
    if (isAuthenticated && !synced) {
      syncWithBackend();
    }
  }, [isAuthenticated, synced, syncWithBackend]);

  // Reset synced state and clear wishlist when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setSynced(false);
      // Clear wishlist when logged out - prevents stale data from showing
      setWishlist([]);
      localStorage.removeItem('coolgirl-wishlist');
    }
  }, [isAuthenticated]);

  const addToWishlist = async (product) => {
    // Require authentication to add to wishlist
    if (!isAuthenticated) {
      setPendingAction({ type: 'add', product });
      setShowLoginModal(true);
      return { success: false, requiresAuth: true };
    }
    
    if (!product || !product.id) {
      console.error('addToWishlist: Invalid product - missing id');
      return { success: false, requiresAuth: false };
    }
    
    setWishlist((prev) => {
      if (prev.find((item) => item.id === product.id)) {
        return prev;
      }
      return [...prev, product];
    });

    // Sync with backend if authenticated
    try {
      await fetch(`${API_URL}/api/auth/favorites/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ item: product })
      });
    } catch (error) {
      console.error('Failed to add favorite to backend:', error);
      toast.error('Failed to save favorite. Please try again.');
    }
    
    return { success: true, requiresAuth: false };
  };

  const removeFromWishlist = async (productId) => {
    // Require authentication to remove from wishlist
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return { success: false, requiresAuth: true };
    }
    
    setWishlist((prev) => prev.filter((item) => item.id !== productId));

    // Sync with backend
    try {
      await fetch(`${API_URL}/api/auth/favorites/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to remove favorite from backend:', error);
      toast.error('Failed to remove favorite. Please try again.');
    }
    
    return { success: true, requiresAuth: false };
  };

  const isInWishlist = (productId) => {
    return wishlist.some((item) => item.id === productId);
  };

  const toggleWishlist = async (product) => {
    if (!product || !product.id) {
      console.error('toggleWishlist: Invalid product - missing id');
      return { added: false, requiresAuth: false };
    }
    
    // Require authentication
    if (!isAuthenticated) {
      setPendingAction({ type: 'toggle', product });
      setShowLoginModal(true);
      return { added: false, requiresAuth: true };
    }
    
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id);
      return { added: false, requiresAuth: false };
    } else {
      await addToWishlist(product);
      return { added: true, requiresAuth: false };
    }
  };

  // Handle successful login - execute pending action
  const handleLoginSuccess = async () => {
    if (pendingAction) {
      const { type, product } = pendingAction;
      setPendingAction(null);
      
      // Small delay to ensure auth state is updated
      setTimeout(async () => {
        if (type === 'add' || type === 'toggle') {
          if (!isInWishlist(product.id)) {
            // Directly add without going through toggleWishlist to avoid re-auth check
            setWishlist((prev) => [...prev, product]);
            try {
              await fetch(`${API_URL}/api/auth/favorites/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ item: product })
              });
            } catch (error) {
              console.error('Failed to add favorite to backend:', error);
              toast.error('Failed to save favorite. Please try again.');
            }
          }
        }
      }, 100);
    }
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
    setPendingAction(null);
  };

  // Open login modal (for external use, e.g., FavoritesPage prompt)
  const openLoginModal = () => {
    setShowLoginModal(true);
  };

  const clearWishlist = async () => {
    setWishlist([]);
    
    // Clear from backend if authenticated
    if (isAuthenticated) {
      try {
        await fetch(`${API_URL}/api/auth/favorites/clear`, {
          method: 'DELETE',
          credentials: 'include'
        });
      } catch (error) {
        console.error('Failed to clear favorites from backend:', error);
      }
    }
  };

  // Update a wishlist item with fresh data (e.g., when product image changes)
  const updateWishlistItem = (productId, freshData) => {
    setWishlist((prev) => 
      prev.map(item => 
        item.id === productId ? { ...item, ...freshData } : item
      )
    );
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        toggleWishlist,
        clearWishlist,
        updateWishlistItem,
        wishlistCount: wishlist.length,
        isAuthenticated,
        loginWithGoogle,
        showLoginModal,
        openLoginModal,
        closeLoginModal,
        handleLoginSuccess,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
